import React, { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";

import { FOCUSABLE, trapFocus } from "../dialogFocus";
import { TEXT } from "./editorText";

/**
 * The editor's dialog shell: title, step bar, scrolling body, footer. Esc closes; focus starts in the dialog and stays in it.
 * Props: stepIndex (0-based position in TEXT.steps), onClose, footer (node), children.
 */
export default function EditorFrame({ stepIndex, onClose, footer, children, heading }) {
  const panelRef = useRef(null);
  const titleId = useId();
  const shown = useRef(false);

  useEffect(() => {
    const previous = document.activeElement;
    const first = panelRef.current?.querySelector(FOCUSABLE);
    if (first) first.focus();
    return () => { if (previous && typeof previous.focus === "function") previous.focus(); };
  }, []);

  // Each screen change unmounts the button that had focus: put it back in the dialog so Esc and Tab keep working.
  useEffect(() => {
    if (!shown.current) { shown.current = true; return; }
    if (panelRef.current) panelRef.current.focus();
  }, [stepIndex]);

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    trapFocus(event, panelRef.current);
  };

  return (
    <div className="le-overlay" onKeyDown={onKeyDown}>
      <div className="le-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panelRef} tabIndex={-1}>
        <header className="le-header">
          <h2 className="le-title" id={titleId}>{heading || TEXT.title}</h2>
          <span className="le-spacer" />
          <button type="button" className="le-icon-btn" onClick={onClose} aria-label={TEXT.close}><X size={16} aria-hidden="true" /></button>
        </header>
        <ol className="le-steps" aria-label={TEXT.progress}>
          {TEXT.steps.map((label, i) => (
            <li key={label} className={`le-step${i === stepIndex ? " is-current" : ""}${i < stepIndex ? " is-done" : ""}`}
              aria-current={i === stepIndex ? "step" : undefined} aria-label={`${TEXT.stage} ${i + 1}: ${label}`}>
              <span className="le-step-btn">
                <span className="le-step-dot" aria-hidden="true">{i + 1}</span>
                <span>{label}</span>
              </span>
            </li>
          ))}
        </ol>
        <div className="le-body">{children}</div>
        <footer className="le-footer">{footer}</footer>
      </div>
    </div>
  );
}
